import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import AuthContext from '../../context/AuthProvider';
import { CircularProgress } from '@mui/material';

const ImpersonationHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useContext(AuthContext);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('No impersonation token provided');
      setLoading(false);
      return;
    }

    const exchangeToken = async () => {
      try {
        const response = await axios.post(
          '/Impersonation/ExchangeToken',
          { token },
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
          }
        );

        const data = response.data;

        const authData = {
          accessToken: data.accessToken,
          userId: data.userId,
          username: data.username,
          fullName: data.fullName,
          email: data.email,
          role: data.role,
          roles: [data.role],
          universityId: data.universityId,
          universityName: data.universityName,
          components: [],

          isImpersonating: true,
          impersonatorId: data.impersonatorId,
          impersonatorName: data.impersonatorName,
          impersonationSessionId: data.impersonationSessionId,
          impersonationExpiresAt: data.impersonationExpiresAt,
          impersonationRemainingMinutes: data.remainingMinutes
        };

        setAuth(authData);
        localStorage.setItem('auth', JSON.stringify(authData));
        localStorage.setItem('isImpersonating', 'true');
        localStorage.setItem('impersonationSessionId', data.impersonationSessionId);
        localStorage.setItem('impersonationExpiresAt', data.impersonationExpiresAt);

        navigate('/SchedulePlan/List', { replace: true });
      } catch (err) {
        console.error('Impersonation token exchange failed:', err);
        const errorMessage = err.response?.data?.message || 'Failed to authenticate impersonation session';
        setError(errorMessage);
        setLoading(false);
      }
    };

    exchangeToken();
  }, [searchParams, setAuth, navigate]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <div className="text-center">
          <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} className="mb-3" />
          <h5>Setting up view session...</h5>
          <p className="text-muted">Please wait while we authenticate your session.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <div className="card shadow" style={{ maxWidth: '500px' }}>
          <div className="card-body text-center p-5">
            <i className="bi bi-exclamation-triangle-fill text-danger fs-1 mb-3"></i>
            <h4 className="mb-3">Session Error</h4>
            <p className="text-muted mb-4">{error}</p>
            <div className="d-grid gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => window.close()}
              >
                Close Window
              </button>
            </div>
            <small className="text-muted mt-3 d-block">
              If this window doesn't close, you can safely close it manually.
            </small>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ImpersonationHandler;
